"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  buildConsentCookie,
  clearConsentCookie,
  CONSENT_EVENT,
  readConsentCookie,
  type ConsentChoice,
} from "@lib/util/consent"

type ConsentState = {
  /**
   * null means "not decided": the banner is shown and nothing optional may
   * load. SSR always renders this state — the cookie is only readable on the
   * client, which keeps every route statically renderable.
   */
  choice: ConsentChoice | null
  /** False until the stored choice has been read on the client. */
  ready: boolean
  decide: (choice: ConsentChoice) => void
  /** Forgets the choice so the banner asks again (Cookie Policy page). */
  reset: () => void
}

const ConsentContext = createContext<ConsentState>({
  choice: null,
  ready: false,
  decide: () => {},
  reset: () => {},
})

const isSecure = () =>
  typeof window !== "undefined" && window.location.protocol === "https:"

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [choice, setChoice] = useState<ConsentChoice | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setChoice(readConsentCookie(document.cookie))
    setReady(true)
  }, [])

  const decide = useCallback((next: ConsentChoice) => {
    document.cookie = buildConsentCookie(next, { secure: isSecure() })
    setChoice(next)
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }))
  }, [])

  const reset = useCallback(() => {
    document.cookie = clearConsentCookie({ secure: isSecure() })
    setChoice(null)
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }))
  }, [])

  const value = useMemo(
    () => ({ choice, ready, decide, reset }),
    [choice, ready, decide, reset]
  )

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  )
}

export const useConsent = () => useContext(ConsentContext)
